import { Auth } from "aws-amplify";
import React, {
  createContext,
  Dispatch,
  MutableRefObject,
  SetStateAction,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";

import { IPerson, RoleUser } from "../interfaces/intarfaces";
import { useFetchedData } from "utils/hooks/useFetch";
import { useLocation, useNavigate } from "react-router-dom";
import { ERROR404, ERRORANY, HOST_PROFILE_ROUTE, LOGIN_ROUTE, STUDENT_PROFILE_ROUTE, } from "utils/constants";
import { getSignedUrl } from "../utils/utilAmplify";
import { ISocketResponse } from "../interfaces/interfacesChat";

interface IContextHospi {
  triggerChatLoad: boolean,
  noChatId: boolean,
  setTriggerChatLoad: (trigger: boolean) => void,
  setNoChatId: (trigger: boolean) => void,
  currentUserID: string | undefined,
  userLoading: boolean;
  loading: boolean | '';
  socketRef: MutableRefObject<WebSocket | null> | null;
  messageData: ISocketResponse | null;
  unreadConversations: string[],
  fromFooter: boolean;
  userStatus: RoleUser | undefined;
  setUser: Dispatch<SetStateAction<string | undefined>>;
  getNameUser: string | undefined;
  getCurrentUserID: string | undefined;
  userProfile: Partial<IPerson> | undefined;
  changeUser: (user: RoleUser | undefined) => void;
  updateProfile: Dispatch<SetStateAction<Partial<IPerson> | undefined>>;
  setUnreadConversations: Dispatch<SetStateAction<string[]>>;
  removeUserAccount: () => void;
  setFromFooter: (flag: boolean) => void,
}

const ContextHospi = createContext<IContextHospi>({
  currentUserID: undefined,
  userLoading: true,
  loading: true,
  triggerChatLoad: false,
  noChatId: false,
  setTriggerChatLoad: () => undefined,
  setNoChatId: () => undefined,
  socketRef: null,
  unreadConversations: [],
  messageData: null,
  fromFooter: false,
  setFromFooter: () => undefined,
  userStatus: undefined,
  setUser: () => console.log("set user"),
  getNameUser: "unknown",
  getCurrentUserID: "",
  userProfile: undefined,
  setUnreadConversations: () => undefined,
  changeUser: (user: RoleUser | undefined) => console.log("change user"),
  updateProfile: () => console.log("change profile"),
  removeUserAccount: () => console.log("Deleted account"),
});

export const useContextHospi = (): IContextHospi => {
  return useContext<IContextHospi>(ContextHospi);
};

export const ContextHospiProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [fromFooter, setFromFooter] = useState<boolean>(false)
  const [currentUser, setCurrentUser] = useState<RoleUser | undefined>(
    undefined
  );
  const [currentUserID, setCurrentUserID] = useState<string | undefined>(
    undefined
  );
  const [profileData, setProfileData] = useState<Partial<IPerson> | undefined>(
    undefined
  );
  const [messageData, setMessageData] = useState<ISocketResponse | null>(null);
  const [unreadConversations, setUnreadConversations] = useState<string[]>([])
  const [waitingToReconnect, setWaitingToReconnect] = useState<boolean>(false);
  const [userLoading, setUserLoading] = useState<boolean>(true)
  const [socketClosed, setSocketClosed] = useState<boolean>(false)
  const [triggerChatLoad, setTriggerChatLoad] = useState<boolean>(false)
  const [noChatId, setNoChatId] = useState<boolean>(false)

  const socketRef = useRef<WebSocket | null>(null);

  useEffect(() => {
    if (messageData?.action === 'text_message_created' && messageData.body.author_id !== profileData?.user_id) {
      setUnreadConversations(prev => [...prev, messageData.body.chat_id])
    } else if (messageData?.action === 'invite_message_created' && messageData.body.author_id !== profileData?.user_id) {
      setUnreadConversations(prev => [...prev, messageData.body.chat_id])
    } else if (messageData?.action === 'message_read') {
      setUnreadConversations(prev => [...prev.filter((id) => id !== messageData.body.chat_id)])
    }
  }, [messageData])

  const { i18n } = useTranslation();

  // GET to SERVER and fetch data Profile user with id = currentUserID
  // Start only when changed state currentUserID
  const { data, error, loading } = useFetchedData(currentUserID);

  useEffect(() => {
    let socket: WebSocket;
    if (waitingToReconnect || !currentUserID) {
      return;
    }
    if (!socketRef.current) {
      console.log(socketRef)
      getSignedUrl()
        .then((signedUrl) => {
          if (signedUrl.length) {
            socket = new WebSocket(signedUrl);
            socketRef.current = socket
            if (socketClosed && socketRef.current) {
              setTriggerChatLoad(true)
              setSocketClosed(false)
            }

            socket.onopen = () => {
              socketRef.current = socket
            }

            socket.onerror = (event) => {
            }

            socket.onmessage = (event) => {
              if (event.data.includes('Not found any Chat with id')) {
                setNoChatId(true);
              } else {
                try {
                  const message: ISocketResponse = JSON.parse(event.data);
                  setMessageData(message);

                } catch { }
              }
            };

            socket.onclose = () => {
              setSocketClosed(true)
              if (socketRef.current) {
                // Connection failed
              } else {
                // Cleanup initiated from app side, can return here, to not attempt to reconnect
                return;
              }
              if (waitingToReconnect) {
                return;
              }

              // Setting this will trigger a re-run of the effect,
              // cleaning up the current websocket, but not setting
              // up a new one right away
              setWaitingToReconnect(true);

              // This will trigger another re-run, and because it is false,
              // the socket will be set up again
              setTimeout(() => setWaitingToReconnect(false), 50);
            };
          }
        })
        .catch((error) => console.error("ERROR Connection: ", error));
    }
    return () => {
      // Dereference, so it will set up next time
      if (socketRef.current) {
        socket.close();
      }
      socketRef.current = null;
    }
  }, [waitingToReconnect, currentUserID])

  useEffect(() => {
    Auth.currentAuthenticatedUser()
      .then(({ attributes }) => {
        setCurrentUserID(attributes.sub);
      })
      .catch(() => { })
      .finally(() => {
        setUserLoading(false)
      });
  }, []);

  useEffect(() => {
    if (data && !Object.keys(data).includes("isAxiosError")) {
      setProfileData(data);
      setCurrentUser(data?.user_type ?? "student");
      // let lang;
      // console.log("default lng " + data.default_language)
      // if (!data.default_language) lang = "GB";
      // else
      //   lang =
      //     data.default_language === "gb"
      //       ? "GB"
      //       : data.default_language.toLocaleUpperCase();
      // i18n.changeLanguage(lang.toLocaleLowerCase());
    } else {
      setCurrentUser(undefined);
      setProfileData(undefined);
    }
    // eslint-disable-next-line
  }, [data]);

  useEffect(() => {
    // If profile is absent
    if (error === ERROR404) {
      Auth.currentAuthenticatedUser().then(({ attributes }) => {
        let curUserRole = currentUser ?? attributes.profile;
        setCurrentUser(curUserRole);
        navigate(
          curUserRole === "student" ? `/${i18n.language}${STUDENT_PROFILE_ROUTE.slice(5)}` : `/${i18n.language}${HOST_PROFILE_ROUTE.slice(5)}`
        );
      });
    } else if (error === ERRORANY) {
      Auth.signOut({ global: true }).then(() => {
        if (location.pathname === LOGIN_ROUTE) navigate(`/${i18n.language}/`);
      });
    }
    // eslint-disable-next-line
  }, [error]);

  const getDataUser = <T extends keyof IPerson>(k: T) => {
    return (profileData as IPerson)?.[k] || "Unknown";
  };

  const changeUser = async (user: RoleUser | undefined) => {
    setCurrentUser(user);
    try {
      const userPrf = await Auth.currentAuthenticatedUser();
      await Auth.updateUserAttributes(userPrf, {
        profile: user,
      });
      user &&
        navigate(
          user === "student" ? `/${i18n.language}${STUDENT_PROFILE_ROUTE.slice(5)}` : `/${i18n.language}${HOST_PROFILE_ROUTE.slice(5)}`
        );
    } catch (error) { }
  };

  const deleteUser = () => {
    Auth.signOut({ global: true }).then(() => {
      setCurrentUserID(undefined);
      setProfileData(undefined);
      navigate(`/${i18n.language}/`);
    });
  };

  return (
    <ContextHospi.Provider
      value={{
        currentUserID,
        userLoading,
        loading,
        setNoChatId,
        noChatId,
        triggerChatLoad,
        setTriggerChatLoad,
        unreadConversations,
        setUnreadConversations,
        socketRef,
        messageData,
        setFromFooter,
        fromFooter,
        userStatus: currentUser,
        setUser: setCurrentUserID,
        getNameUser: getDataUser("first_name"),
        getCurrentUserID: currentUserID,
        userProfile: profileData,
        changeUser,
        updateProfile: setProfileData,
        removeUserAccount: deleteUser,
      }}
    >
      {children}
    </ContextHospi.Provider>
  );
};
